import { createTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Components {
    MuiDataGrid?: {
      styleOverrides?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MUI DataGrid community edition lacks typed style overrides
        root?: any;
      };
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface Palette {}
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface PaletteOptions {}
  interface Theme {
    layout: {
      drawerWidth: number;
    };
    colors: {
      red: string;
      yellow: string;
      orange: string;
      green: string;
      lightGreen: string;
      blue: string;
      grey: string;
    };
    recommendationColors: {
      strong: string;
      normal: string;
      weak: string;
      none: string;
    };
  }
  interface ThemeOptions {
    layout?: {
      drawerWidth?: number;
    };
    colors?: {
      red?: string;
      yellow?: string;
      orange?: string;
      green?: string;
      lightGreen?: string;
      blue?: string;
      grey?: string;
    };
    recommendationColors?: {
      strong?: string;
      normal?: string;
      weak?: string;
      none?: string;
    };
  }
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    lgplus: true;
    xl: true;
  }
}

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0466C8',    // Bright blue
      light: '#0582FC',   // Lighter version
      dark: '#0352A0',    // Darker version
      contrastText: '#fff'
    },
    secondary: {
      main: '#023E7D',    // Navy blue
      light: '#034694',   // Lighter version
      dark: '#01326A',    // Darker version
      contrastText: '#fff'
    },
    // Removed tertiary and subtle palettes
    background: {
      default: '#FFFFFF',
      paper: '#F8FAFC'    // Slight blue tint to paper backgrounds
    },
    divider: 'rgba(0, 0, 0, 0.2)',
    action: {
      selected: '#E3F0FF', // Light blue for selected rows
    },
  },
  components: {
    MuiAppBar: {
      defaultProps: {
        elevation: 0
      },
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          borderBottom: '1px solid rgba(0, 0, 0, 0.2)',  // Lighter border
          color: '#000'
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
          borderRight: '1px solid rgba(0, 0, 0, 0.2)',  // Lighter border
          backgroundColor: '#fff'
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          margin: '4px 8px',
          borderRadius: 4,
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          minWidth: 180,
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.15)',
          backgroundColor: '#FFFFFF',
          paddingTop: '8px',
          border: 'none'
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '8px 16px',
          margin: 0,
          borderRadius: 0,
          fontSize: '14px',
          minHeight: '40px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          '&:hover': {
            backgroundColor: ({ theme }: { theme: Theme }) => alpha(theme.palette.background.paper, 0.9),
          },
          '& .MuiSvgIcon-root': {
            fontSize: '20px',
            color: ({ theme }: { theme: Theme }) => theme.palette.text.primary,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '& .MuiOutlinedInput-root': {
            borderRadius: '5px',
          },
          '& .MuiInputLabel-root': { //this is for a safari issue when multiline field label looks odd when empty and focused
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
              transformOrigin: 'top left',
              backgroundColor: theme.palette.background.default,
              padding: '0 4px'
            },
            '&.MuiInputLabel-outlined.MuiInputLabel-shrink': {
              backgroundColor: theme.palette.background.default
            }
          },
          '& .MuiInputLabel-outlined.MuiInputLabel-shrink.Mui-focused': {
            transform: 'translate(14px, -9px) scale(0.75)',
            backgroundColor: theme.palette.background.default
          }
        })
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '&.MuiInputLabel-shrink': {
            backgroundColor: theme.palette.background.default,
            zIndex: 1000,
            paddingLeft: '4px',
            paddingRight: '4px',
          }
        })
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '5px',
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '5px',
        }
      }
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0
      },
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            padding: 16, // equivalent to p: 2
            marginBottom: 16, // equivalent to mb: 2
            borderRadius: 20,
          },
        }
      ],
    },
    MuiList: {
      defaultProps: {
        dense: true
      },
      styleOverrides: {
        root: {
          paddingTop: 0
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '5px',
          padding: '8px 24px',
          textTransform: 'none',
          whiteSpace: 'nowrap',
          minWidth: 'auto',
          fontWeight: 500,
          boxShadow: 'none',  // Remove default shadow
          '&:hover': {
            boxShadow: 'none'  // Remove shadow on hover too
          }
        }
      },
      variants: [
        {
          props: { variant: 'contained' },
          style: {
            borderRadius: '5px',
            padding: '8px 24px',
            minWidth: '100px',
            boxShadow: 'none',  // Ensure no shadow for contained variant
            '&:hover': {
              boxShadow: 'none'  // No shadow on hover
            }
          }
        }
      ]
    },
    MuiTabs: {
      styleOverrides: {
        root: (props) => {
          const theme = props.theme;
          return {
            borderBottom: `1px solid ${theme.palette.divider}`,
            '& .MuiTouchRipple-root': {
              display: 'none'
            },
            '& .MuiTab-root': {
              transition: 'all 0.2s ease',
              position: 'relative',
              paddingBottom: 0,
              marginRight: 8,
              textTransform: 'none',
              '&:hover': {
                color: theme.palette.primary.main
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                height: '2px',
                backgroundColor: theme.palette.primary.main,
                transition: 'opacity 0.2s ease',
                opacity: 0
              },
              '&:hover::after': {
                opacity: 1
              }
            },
            '& .Mui-selected::after': {
              opacity: 1
            }
          };
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 48,
          position: 'relative',
          '& .MuiSvgIcon-root': {
            fontSize: 18
          }
        }
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          border: 'none',
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            display: 'flex',
            alignItems: 'flex-start',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: alpha(theme.palette.background.default, 0.5),
            borderBottom: 'none',
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.9rem',
            }
          },
          '& .MuiDataGrid-columnHeader': {
            height: '40px !important',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          '& .MuiButtonBase-root': {
            '&:focus': {
              outline: 'none',
            },
            '&:focus-visible': {
              outline: 'none',
            }
          },
          '& .MuiDataGrid-row': {
            '&:hover': {
              backgroundColor: 'transparent',
            },
            minHeight: 'auto !important',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'transparent',
          },
          '& .MuiDataGrid-footer': {
            borderTop: 'none',
          },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: "8px",
          maxWidth: 250
        }
      }
    },
  },
  typography: {
    fontFamily: '"Assistant", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      color: '#0466C8'    // Updated to new primary color
    },
    // Removed unused h2 and h3
    h6: {
      fontWeight: 600
    },
    logo: {
      fontFamily: '"Assistant", sans-serif',
      fontWeight: 700,
      fontSize: '2rem',
      letterSpacing: 'tight',
      background: `linear-gradient(to right, #33415C, #023E7D, #0466C8)`,  // new gradient
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      textFillColor: 'transparent'
    } as React.CSSProperties
  },
  shape: {
    borderRadius: 0
  },
  spacing: 8,
  layout: {
    drawerWidth: 300
  } as const,
  colors: {
    red: '#dc2626',
    orange: '#e67e00',
    yellow: '#fbbf24',
    lightGreen: '#238636',
    green: '#0B6B2B',
    blue: '#0466C8',
    grey: '#6b7280',
  },
  recommendationColors: {
    strong: "#0B6B2B",
    normal: "#238636",
    weak: "#FFAA00",
    none: "#a0a0a0",
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      lgplus: 1440,
      xl: 1536,
    },
  },
});

// Add type definition for our custom variant
declare module '@mui/material/styles' {
  interface TypographyVariants {
    logo: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    logo?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    logo: true;
  }
}

export default lightTheme;
